import { trpc } from "@/lib/trpc";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { User, TrendingUp, BookOpen, Calendar, ChevronDown, ChevronUp } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";

const DAYS_DE = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"];

function getAttendanceColor(rate: number): string {
  if (rate >= 80) return "bg-green-500";
  if (rate >= 50) return "bg-yellow-500";
  return "bg-red-500";
}

function getAttendanceTextColor(rate: number): string {
  if (rate >= 80) return "text-green-600 dark:text-green-400";
  if (rate >= 50) return "text-yellow-600 dark:text-yellow-400";
  return "text-red-600 dark:text-red-400";
}

export function InstructorAttendanceReport() {
  const { data, isLoading } = trpc.session.getInstructorAttendanceOverview.useQuery();
  const [expandedInstructors, setExpandedInstructors] = useState<Set<number>>(new Set());
  
  const toggleExpanded = (instructorId: number) => {
    setExpandedInstructors(prev => {
      const newSet = new Set(prev);
      if (newSet.has(instructorId)) {
        newSet.delete(instructorId);
      } else {
        newSet.add(instructorId);
      }
      return newSet;
    });
  };
  
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5 text-primary" />
            Anwesenheit nach Kursleiter
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="space-y-2">
              <Skeleton className="h-12 w-full" />
            </div>
          ))}
        </CardContent>
      </Card>
    );
  }
  
  if (!data || data.instructors.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5 text-primary" />
            Anwesenheit nach Kursleiter
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground text-sm">Keine Kursleiter gefunden.</p>
        </CardContent>
      </Card>
    );
  }
  
  // Filter out instructors with no courses
  const instructorsWithCourses = data.instructors.filter(i => i.totalCourses > 0);
  
  if (instructorsWithCourses.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5 text-primary" />
            Anwesenheit nach Kursleiter
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground text-sm">Keine Kursleiter mit aktiven Kursen gefunden.</p>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <User className="h-5 w-5 text-primary" />
          Anwesenheit nach Kursleiter
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {instructorsWithCourses.map(instructor => {
          const isExpanded = expandedInstructors.has(instructor.instructorId);
          const hasAttendanceData = instructor.totalPresent + instructor.totalAbsent > 0;
          
          return (
            <div 
              key={instructor.instructorId} 
              className="border rounded-lg overflow-hidden"
            >
              {/* Instructor Header */}
              <button
                onClick={() => toggleExpanded(instructor.instructorId)}
                className="w-full flex items-center justify-between p-4 hover:bg-muted/50 transition-colors text-left"
              >
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10">
                    <User className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-medium">{instructor.instructorName}</h3>
                    <div className="flex items-center gap-3 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <BookOpen className="h-3.5 w-3.5" />
                        {instructor.totalCourses} Kurs{instructor.totalCourses !== 1 ? "e" : ""}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3.5 w-3.5" />
                        {instructor.totalSessions} Termin{instructor.totalSessions !== 1 ? "e" : ""}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  {hasAttendanceData ? (
                    <div className="flex items-center gap-2">
                      <TrendingUp className={`h-4 w-4 ${getAttendanceTextColor(instructor.averageAttendanceRate)}`} />
                      <span className={`font-semibold ${getAttendanceTextColor(instructor.averageAttendanceRate)}`}>
                        {instructor.averageAttendanceRate}%
                      </span>
                    </div>
                  ) : (
                    <span className="text-sm text-muted-foreground">–</span>
                  )}
                  {isExpanded ? (
                    <ChevronUp className="h-5 w-5 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-muted-foreground" />
                  )}
                </div>
              </button>
              
              {/* Expanded Course Details */}
              {isExpanded && instructor.courses.length > 0 && (
                <div className="border-t bg-muted/30 p-4 space-y-3">
                  {instructor.courses.map(course => {
                    const hasCourseData = course.totalSessions > 0 && course.attendanceRate > 0;
                    
                    return (
                      <div key={course.courseId} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-medium text-muted-foreground w-6">
                            {DAYS_DE[course.dayOfWeek]}
                          </span>
                          <span className="text-sm">{course.courseName}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          {hasCourseData ? (
                            <>
                              <div className="w-24 h-2 bg-muted rounded-full overflow-hidden">
                                <div 
                                  className={`h-full ${getAttendanceColor(course.attendanceRate)} transition-all`}
                                  style={{ width: `${course.attendanceRate}%` }}
                                />
                              </div>
                              <span className={`text-sm font-medium w-12 text-right ${getAttendanceTextColor(course.attendanceRate)}`}>
                                {course.attendanceRate}%
                              </span>
                            </>
                          ) : (
                            <>
                              <div className="w-24 h-2 bg-muted rounded-full" />
                              <span className="text-sm text-muted-foreground w-12 text-right">–</span>
                            </>
                          )}
                          <span className="text-xs text-muted-foreground w-12 text-right">
                            {course.totalSessions} Termin{course.totalSessions !== 1 ? "e" : ""}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                  
                  {/* Summary Row */}
                  {hasAttendanceData && (
                    <div className="flex items-center justify-between pt-3 border-t">
                      <span className="text-sm font-medium">Gesamt</span>
                      <div className="flex items-center gap-2 text-sm">
                        <span className="text-green-600 dark:text-green-400">
                          {instructor.totalPresent} anwesend
                        </span>
                        <span className="text-muted-foreground">•</span>
                        <span className="text-red-600 dark:text-red-400">
                          {instructor.totalAbsent} abwesend
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
